<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { $router } from '@/router'
import { $bus } from '@/common/events'
import { dialog } from '@/common/dialog'
import { toast } from '@/common/toast'
import { useTitle } from '@/composables/title'
import { useTranslate } from '@/composables/language'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { canInterrupt } from '@/composables/interrupt'
import { useCabinetAlarm } from '@/composables/alarm'
import { useMaintenance } from '@/composables/maintenance'
import PushButton from '@/components/PushButton.vue'
import InsertIndicator from '@/components/InsertIndicator.vue'
import Fullscreen from '@/components/Fullscreen.vue'

const { t } = useTranslate();

onMounted(() => {
  useTitle(t('admin.ui_test'));
  useMenu("admin");
  useSubmenu(false);
  useTopBar(true);
  canInterrupt(false);
})

onUnmounted(() => {
  canInterrupt(true);
});

const text = ref("");
const counter = ref(0);

const isFullscreenMode = ref(false);

function startFullscreenMode() {
  isFullscreenMode.value = true;
}

function stopFullscreenMode() {
  isFullscreenMode.value = false;
  isFontSize1.value = false;
  isFontSize2.value = false;
  isFontSize3.value = false;
}

async function showDialog(type) {
  switch (type) {
    case "question":
      let result = await dialog({
        persistent: true,
        type: "question",
        title: t('components.question_title'),
        text: t('components.question_text'),
        confirmButtonText: t('generic.delete'),
        cancelButtonText: t('generic.cancel'),
      });
      console.log("Question dialog result", result);
      break;

    case "info":
      dialog({
        type: "info",
        title: t('components.info_title'),
        text: t('components.info_text'),
        confirmButtonText: t('generic.close'),
      });
      break;

    case "success":
      dialog({
        type: "success",
        //title: "Potrditev",
        text: t('components.success_text'),
        confirmButtonText: t('generic.ok'),
      });
      break;

    case "warning":
      dialog({
        type: "warning",
        title: t('components.warning_title'),
        text: t('components.warning_text'),
        confirmButtonText: t('generic.close'),
      });
      break;

    case "error":
      dialog({
        type: "danger",
        title: t('components.error_title'),
        text: t('components.error_text'),
        confirmButtonText: t('generic.ok'),
      });
      break;
  }
}

function showToast(type) {
  switch (type) {
    case "info":
      toast({
        type: "info",
        text: "Information goes here.",
        canClose: true,
        timeout: 500
      });
      break;

    case "success":
      toast({
        type: "success",
        text: t('components.success_toast'),
        canClose: true,
        //timeout: 10000
      });
      break;

    case "warning":
      toast({
        type: "warning",
        text: "Session will expire soon.",
        canClose: true,
        //timeout: 10000
      });
      break;

    case "error":
      toast({
        type: "danger",
        text: "Session expired!",
        canClose: true,
        //timeout: 10000
      });
      break;

    case "loading":
      toast({
        type: "loading",
        text: "Please, wait...",
        canClose: true,
        timeout: 5000
      });
      break;
  }
}

function clearToast() {
  toast({ clearAll: true });
}

function goBack() {
  try {
    $router.push({ name: "Admin" });
  }
  catch (err) {
    dialog(err);
  }
}

const isFontSize1 = ref(false);
const isFontSize2 = ref(false);
const isFontSize3 = ref(false);

const dropDownOptions = ref([
  { id: null, name: "None" },
  { id: "1", name: "Option 1" },
  { id: "2", name: "Option 2" },
  { id: "3", name: "Option 3" },
  { id: "4", name: "Long Long Text" },
]);

const dropDownSelect1 = ref();
const dropDownSelect2 = ref("1");
const dropDownSelect3 = ref("2");

function showCabinetAlarm() {
  useCabinetAlarm({ alarm: "halt" });
  setTimeout(() => {
    useCabinetAlarm(false);
  }, 5000);
}

function showMaintenance() {
  useMaintenance({ mode: "maintenance" });
  setTimeout(() => {
    useMaintenance(false);
  }, 5000);
}
</script>

<template>
  <div class="relative min-h-screen dark:bg-gray-900 dark:text-white p-5">

    <!-- Header -->
    <div class="w-full mx-auto flex items-center justify-between">
      <div class="text-lg font-bold dark:text-white">User interface test</div>
      <div class="flex flex-row justify-items-strech space-x-2">
        <Button type="secondary" @click="goBack()">Back</Button>
        <Button type="primary" @click="showCabinetAlarm()">Alarm</Button>
        <Button type="primary" @click="showMaintenance()">Maintenance</Button>
      </div>
    </div>

    <div class="grid place-items-start mt-4">
      
      <div class="flex flex-col gap-4">
        <div>
          <Label>TextBox</Label>
          <div>
            <TextBox v-model="text" placeholder="Type something..."></TextBox>
            <label class="block mt-2 text-sm font-medium text-gray-900 dark:text-white">{{ text }}</label>
          </div>
          <div class="mt-2">
            <TextBox v-model="text" placeholder="Read only" :readonly="true" class="w-full"></TextBox>
          </div>
          <div class="mt-2">
            <TextBox v-model="text" placeholder="Inactive" :enabled="false" class="w-full"></TextBox>
          </div>
          <div class="mt-2">
            <AutocompleteTextBox v-model="text" placeholder="Search..." :enabled="false" :resizable="false" :auto-search="true" class="w-full"></AutocompleteTextBox>
          </div>
        </div>

        <div>
          <Label>DropDown</Label>
          <div>
            <DropDown placeholder="Izberi predal" :options="dropDownOptions" v-model="dropDownSelect1" :enabled="true" :readonly="false" class="w-80" />
          </div>
          <div class="mt-2">
            <DropDown placeholder="Izberi predal" :options="dropDownOptions" v-model="dropDownSelect2" :enabled="false" :readonly="false" class="w-80" />
          </div>
          <div class="grid grid-cols-4 gap-2 mt-2 justify-stretch" style="width: 400px;">
            <DropDown placeholder="Select option" :options="dropDownOptions" v-model="dropDownSelect3" :enabled="true" :readonly="true" />
            <DropDown placeholder="Select option" :options="dropDownOptions" v-model="dropDownSelect3" :enabled="false" :readonly="true" />
            <DropDown placeholder="Select option" :options="dropDownOptions" :enabled="true" :readonly="true" />
            <DropDown placeholder="Select option" :options="dropDownOptions" v-model="dropDownSelect1" />
          </div>
        </div>

        <!-- <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Button - normal button, press and release</label>
          <div class="flex flex-row space-x-2">
            <Button @click="startFullscreenMode">Fullscreen mode</Button>
            <Button type="secondary">Secondary</Button>
            <Button type="danger">Danger</Button>
            <Button type="warning">Warning</Button>
            <Button type="success">Success</Button>
          </div>
        </div> -->

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">PushButton - keep holding to count</label>
          <div class="flex flex-row space-x-2">
            <PushButton type="primary" @click="() => counter++">Push and hold</PushButton>
            <div class="bg-amber-300 rounded-lg px-7 py-2 text-center m-0">
              {{counter}}
            </div>
          </div>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Dialog modal window</label>
          <div class="flex flex-row gap-2">
            <Button @click="showDialog('question')">Question</Button>
            <Button @click="showDialog('info')">Info</Button>
            <Button @click="showDialog('success')" type="success">Success</Button>
            <Button @click="showDialog('warning')" type="warning">Warning</Button>
            <Button @click="showDialog('error')" type="danger">Error</Button>
          </div>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Toast notification</label>
          <div class="flex flex-row gap-2">
            <Button @click="showToast('loading')">Loading</Button>
            <Button @click="showToast('info')">Info</Button>
            <Button @click="showToast('success')" type="success">Success</Button>
            <Button @click="showToast('warning')" type="warning">Warning</Button>
            <Button @click="showToast('error')" type="danger">Error</Button>
            <Button @click="clearToast()" type="secondary">Clear all</Button>
          </div>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Tags (badges)</label>
          <div class="flex flex-row gap-2">
            <Tag>Default</Tag>
            <Tag color="red">Red</Tag>
            <Tag color="yellow">Yellow</Tag>
            <Tag color="green">Green</Tag>
            <Tag color="blue">Blue</Tag>
            <Tag color="purple">Purple</Tag>
          </div>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Insert indicator</label>
          <InsertIndicator units="3,5,2"></InsertIndicator>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Spinner</label>
          <div class="flex flex-row gap-2 justify-start">
            <Spinner />
            <Spinner color="green" />
            <Spinner color="yellow" />
          </div>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Size comparison</label>
          <!-- <div class="flex flex-row space-x-2">
            <Button type="primary" @click="() => isFontSize1 = true">Font size 1</Button>
            <Button type="primary" @click="() => isFontSize2 = true">Font size 2</Button>
            <Button type="primary" @click="() => isFontSize3 = true">Font size 3</Button>
          </div> -->
          <div class="grid gap-2">
            <Button type="primary" size="md">Default button size</Button>
            <Button type="primary" size="lg">Large</Button>
            <Button type="primary" size="xl">Extra Large</Button>
          </div>
        </div>

      </div>
      
    </div>

    <Fullscreen v-if="isFullscreenMode">
      <div class="h-screen w-full bg-white grid place-items-center" @click="stopFullscreenMode">
        <span>Fullscreen mode</span>
      </div>
    </Fullscreen>

    <Fullscreen v-if="isFontSize1">
      <div class="relative h-screen w-full bg-white grid place-items-center" @click="stopFullscreenMode">
        <div class="grid place-items-center h-full" style="height: 80vh;">
          <div class="text-center mx-auto">
            <div>
              <div class="mb-4 font-bold">{{ $t('components.glove_demo') }}</div>
              <div class="grid place-items-center text-gray-200 mx-auto mb-4">
                <div style="font-size: 48px;">
                  <i class="fa fa-hand fa-regular"></i>
                </div>
              </div>
            </div>
            <div>
              <div class="mb-4">{{ $t('components.available_demo') }}</div>
              <div><Button type="primary" class="w-48 h-16" >{{ $t('components.open_tray_demo') }}</Button></div>
            </div>
          </div>
        </div>
      </div>
    </Fullscreen>

    <Fullscreen v-if="isFontSize2">
      <div class="relative h-screen w-full bg-white grid place-items-center" @click="stopFullscreenMode">
        <div class="grid place-items-center h-full" style="height: 80vh;">
          <div class="text-center mx-auto">
            <div>
              <div class="mb-4 font-bold text-xl">{{ $t('components.glove_demo') }}</div>
              <div class="grid place-items-center text-gray-200 mx-auto mb-4">
                <div style="font-size: 56px;">
                  <i class="fa fa-hand fa-regular"></i>
                </div>
              </div>
            </div>
            <div>
              <div class="mb-4 text-lg">{{ $t('components.available_demo') }}</div>
              <div><Button type="primary" class="w-48 h-16" ><span class="text-lg">{{ $t('components.open_tray_demo') }}</span></Button></div>
            </div>
          </div>
        </div>
      </div>
    </Fullscreen>

    <Fullscreen v-if="isFontSize3">
      <div class="relative h-screen w-full bg-white grid place-items-center" @click="stopFullscreenMode">
        <div class="grid place-items-center h-full" style="height: 80vh;">
          <div class="text-center mx-auto">
            <div>
              <div class="mb-4 font-bold text-2xl">{{ $t('components.glove_demo') }}</div>
              <div class="grid place-items-center text-gray-200 mx-auto mb-4">
                <div style="font-size: 64px;">
                  <i class="fa fa-hand fa-regular"></i>
                </div>
              </div>
            </div>
            <div>
              <div class="mb-4 text-xl">{{ $t('components.available_demo') }}</div>
              <div><Button type="primary" class="w-56 h-20" ><span class="text-xl">{{ $t('components.open_tray_demo') }}</span></Button></div>
            </div>
          </div>
        </div>
      </div>
    </Fullscreen>
    
  </div>
</template>

<style scoped>
</style>
